import { useCallback, useMemo } from 'react';

import classnames from 'classnames';
import omit from 'lodash/omit';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';

import { SECTIONS, CATEGORIES, COMPONENTS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityProfileSelector, { VARIANTS } from 'components/Globals/EntityProfileSelector';
import AggregationField from 'components/Globals/FormFields/AggregationField';

import { FILTER_AGGREGATION_SEARCH, ORGANIZATIONS } from 'constants/endpointConstants';
import { TP } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';

import { useTranslation } from 'src/i18n';

import { AGGREGATION_TYPES, ENTITY_TYPES, FILTER_TYPES } from 'operabase-router/constants';

import classes from './IndexPageFilters.module.scss';

const professionTrackingData = {
  section: SECTIONS.MANAGER_LIST,
  component: COMPONENTS.FILTERS,
  category: CATEGORIES.SEARCH,
};

const orgTypeTrackingData = {
  section: SECTIONS.MANAGER_LIST,
  component: COMPONENTS.FILTERS,
  category: CATEGORIES.SEARCH,
  subComponent: SUB_COMPONENTS.ORGANIZATION_TYPE,
};

const searchTrackingData = {
  section: SECTIONS.MANAGER_LIST,
  component: COMPONENTS.FILTERS,
  category: CATEGORIES.SEARCH,
};

const IndexPageFilters = ({ sticky, className, hideSearch = false }) => {
  const { t } = useTranslation('translation');
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext;
  const filters = linkProps?.filters;
  const navigate = useNavigate();
  const { country, applyFilters } = usePageContext();

  const onCountryChange = useCallback(
    selectedCountry => {
      if (selectedCountry) {
        navigate.to(
          navigate.getLinkProps({
            ...linkProps,
            filters: {
              ...filters,
              [FILTER_TYPES.COUNTRY]: [
                {
                  entity: selectedCountry,
                  entityType: ENTITY_TYPES.COUNTRY,
                },
              ],
            },
            onlyLinkProps: true,
          }),
        );
      } else {
        navigate.to(
          navigate.getLinkProps({
            ...linkProps,
            filters: {
              ...omit(filters, [FILTER_TYPES.COUNTRY]),
            },
            onlyLinkProps: true,
          }),
        );
      }
    },
    [navigate, linkProps, filters],
  );

  const onSelectProfession = useCallback(
    selectedProfession => {
      if (selectedProfession) {
        navigate.to(
          navigate.getLinkProps({
            ...linkProps,
            filters: {
              ...filters,
              [FILTER_TYPES.PROFESSION]: [
                {
                  entity: selectedProfession,
                  entityType: ENTITY_TYPES.PROFESSION,
                },
              ],
            },
            onlyLinkProps: true,
          }),
        );
      } else {
        navigate.to(
          navigate.getLinkProps({
            ...linkProps,
            filters: {
              ...omit(filters, [FILTER_TYPES.PROFESSION]),
            },
            onlyLinkProps: true,
          }),
        );
      }
    },
    [navigate, linkProps, filters],
  );

  const onChangeOrgType = useCallback(
    selectedOrgType => {
      if (selectedOrgType) {
        navigate.to(
          navigate.getLinkProps({
            ...linkProps,
            filters: {
              ...filters,
              [FILTER_TYPES.ORGANIZATION_TYPE]: [
                {
                  entity: selectedOrgType,
                  entityType: ENTITY_TYPES.ORGANIZATION_TYPE,
                },
              ],
            },
            onlyLinkProps: true,
          }),
        );
      } else {
        navigate.to(
          navigate.getLinkProps({
            ...linkProps,
            filters: {
              ...omit(filters, [FILTER_TYPES.ORGANIZATION_TYPE]),
            },
            onlyLinkProps: true,
          }),
        );
      }
    },
    [navigate, linkProps, filters],
  );

  const onSearch = useCallback(
    query => {
      applyFilters({
        entityType: ENTITY_TYPES.AGENCY,
        filters: {
          [FILTER_TYPES.PAGE]: 1,
          [FILTER_TYPES.QUERY]: query,
        },
      });
    },
    [applyFilters],
  );

  const onChange = useCallback(
    ({ entity, entityType }) => {
      if (entity?.id && entityType) {
        navigate.to(
          navigate.getLinkProps({
            entityType,
            entity,
          }),
        );
      } else {
        navigate.to(navigate.getLinkProps({ entityType: ENTITY_TYPES.AGENCY }));
      }
    },
    [navigate],
  );

  const selectedProfession = useMemo(() => filters?.[FILTER_TYPES.PROFESSION]?.[0]?.entity, [filters]);
  const selectedOrgType = useMemo(() => filters?.[FILTER_TYPES.ORGANIZATION_TYPE]?.[0]?.entity, [filters]);

  const Wrapper = sticky ? HorizontalScroller : 'div';

  return (
    <Wrapper outset>
      <div className={classnames(classes.filters, { [classes.filters__sticky]: sticky })}>
        <div className={classnames(classes.filters__fields, className)}>
          {!hideSearch && (
            <div className={classes.filters__search}>
              <EntityProfileSelector
                className={classes.filters__search_field}
                searchQuery={filters?.[FILTER_TYPES.QUERY]}
                allowedEntityTypes={[ENTITY_TYPES.AGENCY]}
                variant={VARIANTS.ROUNDED}
                trackingData={{
                  ...searchTrackingData,
                  subComponent: SUB_COMPONENTS.ENTITY_PROFILE_SELECTOR,
                }}
                onChange={onChange}
                onSearch={onSearch}
              />
            </div>
          )}

          <AggregationField
            {...(!selectedProfession && {
              label: t(`${TP}.FN_PROFESSION`),
            })}
            textFieldProps={{ typingPlaceholder: t(`${TP}.FN_SELECT`) }}
            variant="standard"
            endpoint={FILTER_AGGREGATION_SEARCH(ORGANIZATIONS)}
            aggregationType={AGGREGATION_TYPES.REPRESENTEE_PROFESSION}
            selectedOptions={
              selectedProfession ? [{ id: selectedProfession?.id, label: selectedProfession?.name }] : []
            }
            onChange={onSelectProfession}
            queryParams={{
              limit: 100,
              organization_type_id: 70,
            }}
            styles={{
              root: classes.professionDropdown,
            }}
            trackingData={professionTrackingData}
          />

          <AggregationField
            {...(!selectedOrgType && {
              label: t(`${TP}.FN_ORG_TYPE`),
            })}
            textFieldProps={{ typingPlaceholder: t(`${TP}.FN_SELECT`) }}
            variant="standard"
            endpoint={FILTER_AGGREGATION_SEARCH(ORGANIZATIONS)}
            aggregationType={AGGREGATION_TYPES.REPRESENTEE_ORGANIZATION_TYPE}
            selectedOptions={selectedOrgType ? [{ id: selectedOrgType?.id, label: selectedOrgType?.name }] : []}
            onChange={onChangeOrgType}
            queryParams={{
              limit: 100,
              organization_type_id: 70,
            }}
            styles={{
              root: classes.professionDropdown,
            }}
            trackingData={orgTypeTrackingData}
          />

          <AggregationField
            {...(!country && {
              label: t(`${TP}.m_COUNTRY`),
            })}
            textFieldProps={{ typingPlaceholder: t(`${TP}.FN_SELECT`) }}
            variant="standard"
            endpoint={FILTER_AGGREGATION_SEARCH(ORGANIZATIONS)}
            aggregationType={AGGREGATION_TYPES[FILTER_TYPES.COUNTRY]}
            selectedOptions={country ? [{ id: country?.id, label: country?.name }] : []}
            onChange={onCountryChange}
            queryParams={{
              limit: 100,
              organization_type_id: 70,
            }}
            styles={{
              root: classes.countryDropdown,
            }}
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default IndexPageFilters;
